const CAP9 = {
  id: 9,
  slug: "cap9-consenso-tecnologia-blockchain",
  title: "Capítulo 9 — El consenso en la tecnología blockchain",
  sections: [
    {
      heading: "¿Qué es el consenso?",
      text: [
        "El consenso en blockchain es el **proceso mediante el cual todos los participantes de la red (nodos)** llegan a un acuerdo colectivo sobre el **estado de la información y las transacciones registradas**.",
        "En una blockchain no existe una autoridad central que valide, supervise o apruebe las transacciones. Por eso, es necesario un mecanismo que permita que **todos los nodos mantengan una única versión confiable y válida de la cadena de bloques**.",
        "El consenso es esencial porque garantiza la **seguridad, la fiabilidad y la confianza** dentro del sistema, incluso cuando los participantes no se conocen entre sí o pueden tener intereses diferentes."
      ],
      bluepoints: [
        "📌 En blockchain nadie 'manda', pero todos deben estar de acuerdo.",
        "📌 El consenso permite que la verdad sea decidida por la red, no por una autoridad.",
        "📌 Sin consenso, la blockchain sería vulnerable a errores, fraudes o manipulaciones."
      ]
    },
    {
      heading: "¿Por qué el consenso es tan necesario?",
      text: [
        "En una red tradicional, como un banco o una empresa, existe un servidor central que controla, valida y actualiza la información.",
        "En blockchain eso no existe: **todos los nodos tienen una copia del libro mayor**, por lo que todos deben ponerse de acuerdo sobre qué transacciones son válidas y cuáles no.",
        "Ese acuerdo colectivo se obtiene mediante el consenso, que es la base para que blockchain sea **descentralizada, transparente y resistente a manipulaciones**."
      ]
    },
    {
      heading: "Los algoritmos de consenso",
      text: [
        "Los **algoritmos de consenso** son los mecanismos o protocolos que permiten que los nodos de una blockchain lleguen a un acuerdo sobre el estado de la red.",
        "Gracias a ellos, la blockchain puede funcionar **sin depender de ninguna autoridad central**, manteniendo la **integridad, la transparencia y la seguridad** de los datos.",
        "Estos algoritmos definen cómo se valida un bloque, quién puede proponerlo, cómo se evita el doble gasto y cómo se recompensa a quienes aseguran la red."
      ],
      list: [
        "**Proof of Work (PoW)** — Prueba de Trabajo.",
        "**Proof of Stake (PoS)** — Prueba de Participación.",
        "**Delegated Proof of Stake (DPoS)** — Prueba de Participación Delegada.",
        "**Proof of Authority (PoA)** — Prueba de Autoridad.",
        "Otros: PoH, dPoW, PoB, etc."
      ]
    },
    {
      heading: "¿Qué hacen exactamente los algoritmos de consenso?",
      text: [
        "Los algoritmos de consenso tienen como función principal asegurar que **todos los nodos de la red estén de acuerdo sobre la misma versión de la blockchain**, manteniendo un **libro mayor único, seguro y confiable**.",
        "También ayudan a que las transacciones se validen de forma **transparente, única y definitiva**, evitando conflictos como el **doble gasto**, los **bloques falsos** o los **intentos de manipulación**."
      ],
      bluepoints: [
        "🔐 Garantizan la seguridad de la red.",
        "🧾 Mantienen una única versión del libro mayor.",
        "🚫 Evitan transacciones falsas o duplicadas.",
        "⚙️ Permiten que la red funcione sin intermediarios."
      ]
    },
    {
      heading: "Las reglas del consenso",
      text: [
        "Además de algoritmos, el consenso se basa en un conjunto de **reglas predefinidas** que determinan cómo los nodos deben validar transacciones y bloques.",
        "Estas reglas son implementadas por el **software cliente de la blockchain**, y todos los nodos deben seguirlas para poder participar en la red.",
        "Las reglas definen, por ejemplo:"
      ],
      bluepoints: [
        "⏳ Tiempo promedio entre bloques.",
        "📦 Tamaño máximo de un bloque.",
        "🔐 Algoritmo de consenso utilizado.",
        "✍️ Requisitos de firma y validación.",
        "⚙️ Normas para agregar un bloque a la cadena."
      ]
    },
    {
      heading: "¿Quién define estas reglas?",
      text: [
        "En las blockchains públicas, las reglas del consenso generalmente son decididas por la **comunidad de desarrolladores, usuarios y validadores**, mediante procesos transparentes y abiertos.",
        "Esto permite que cualquier cambio importante en la red (como el tamaño de bloque, tarifas, actualizaciones o bifurcaciones) se realice de forma **democrática y descentralizada**, evitando que una sola entidad tenga el control."
      ],
      bluepoints: [
        "🤝 Las reglas no las dicta una empresa, sino la comunidad.",
        "🌐 Los nodos aceptan cambios cuando hay consenso social y técnico.",
        "🛡️ Esta gobernanza abierta ayuda a mantener la confianza y la descentralización."
      ]
    },
    {
      heading: "En resumen",
      text: [
        "El consenso es el proceso fundamental que permite que una blockchain sea **segura, confiable, transparente y descentralizada**.",
        "Gracias al consenso, los nodos pueden **coordinarse, validar transacciones y llegar a acuerdos**, incluso sin conocerse entre sí.",
        "Sin consenso, simplemente **no existiría blockchain**."
      ],
      bluepoints: [
        "🔑 El consenso es el corazón de blockchain.",
        "🛡️ Protege la información sin necesidad de intermediarios.",
        "🌍 Permite que todos confíen en los datos, aunque no confíen entre ellos."
      ]
    }
  ]
};

export default CAP9;
